/**
 * Session end-goal + definition-of-done. At the start of a planned session the
 * user's request is distilled into an explicit goal statement plus acceptance
 * criteria (or ONE clarifying question when too vague). The goal is persisted
 * as a pipeline checkpoint, feeds the planner, and after each major build step
 * progress is re-evaluated against the criteria — the build STOPS when all are
 * met instead of continuing open-endedly.
 */
import { effortToReasoning, type EffortLevel } from '@fim/futurex-protocol';

/** Minimal model surface (injectable for tests). */
export interface ChatClient {
  chat(request: {
    system?: string;
    messages: { role: 'system' | 'user' | 'assistant'; content: string }[];
    reasoning?: boolean | { effort?: 'high' | 'max' };
    model?: string;
    maxTokens?: number;
  }): Promise<string>;
}

export interface AcceptanceCriterion {
  readonly id: string;
  readonly text: string;
}

export interface SessionGoal {
  /** One-sentence end-goal statement. */
  readonly statement: string;
  readonly criteria: readonly AcceptanceCriterion[];
  /** The original user request the goal was derived from. */
  readonly task: string;
}

export type GoalResult =
  | { readonly kind: 'goal'; readonly goal: SessionGoal }
  | { readonly kind: 'clarify'; readonly question: string };

const GOAL_SYSTEM = [
  'You distill a coding request into an explicit end goal and a definition of done.',
  'Respond with ONLY one minified JSON object — no prose, no code fence — that is EITHER',
  '{"kind":"goal","statement":string,"criteria":string[]}',
  'OR {"kind":"clarify","question":string} when the request is too vague to state a goal confidently.',
  'The statement is one sentence. Criteria are 2-6 concrete, checkable conditions.',
  'Ask at most ONE clarifying question, and only when truly necessary.',
].join('\n');

function extractJson(raw: string): Record<string, unknown> | null {
  const start = raw.indexOf('{');
  const end = raw.lastIndexOf('}');
  if (start === -1 || end <= start) return null;
  try {
    return JSON.parse(raw.slice(start, end + 1)) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/** Parse a goal reply (defensive — model output is untrusted). */
export function parseGoalResult(raw: string, task: string): GoalResult {
  const json = extractJson(raw);
  if (!json) return { kind: 'clarify', question: 'Could you restate what you want built, in one or two sentences?' };
  if (json.kind === 'clarify') {
    const question = typeof json.question === 'string' && json.question ? json.question : 'Could you add more detail to the request?';
    return { kind: 'clarify', question };
  }
  const statement = typeof json.statement === 'string' ? json.statement.trim() : '';
  const criteria = (Array.isArray(json.criteria) ? json.criteria : [])
    .filter((c): c is string => typeof c === 'string' && c.trim().length > 0)
    .map((text, i) => ({ id: `c${i + 1}`, text: text.trim() }));
  if (!statement || criteria.length === 0) {
    return { kind: 'clarify', question: 'What does "done" look like for this task?' };
  }
  return { kind: 'goal', goal: { statement, criteria, task } };
}

/** Derive the session's end goal + acceptance criteria from the request. */
export async function deriveGoal(
  task: string,
  context: string,
  ai: ChatClient,
  opts: { model: string; effort: EffortLevel; maxTokens?: number },
): Promise<GoalResult> {
  const reply = await ai.chat({
    system: GOAL_SYSTEM,
    messages: [{ role: 'user', content: `CONTEXT:\n${context.slice(0, 4000)}\n\nREQUEST:\n${task}` }],
    reasoning: effortToReasoning(opts.effort),
    model: opts.model,
    maxTokens: opts.maxTokens ?? 1000,
  });
  return parseGoalResult(reply, task);
}

export interface GoalEvaluation {
  /** Criterion ids judged satisfied by the evidence so far. */
  readonly met: readonly string[];
  /** Criterion ids still outstanding. */
  readonly remaining: readonly string[];
  readonly summary: string;
  /** True when every criterion is met — the signal to stop building. */
  readonly done: boolean;
}

const EVAL_SYSTEM = [
  'You judge progress against a definition of done.',
  'Given acceptance criteria (each with an id) and evidence of work performed, respond with ONLY one',
  'minified JSON object {"met":string[],"remaining":string[],"summary":string} where met/remaining',
  'partition the criterion ids. Be strict: a criterion is met only when the evidence shows it.',
].join('\n');

/** Parse an evaluation reply; on garbage, fail safe (nothing met). */
export function parseGoalEvaluation(raw: string, goal: SessionGoal): GoalEvaluation {
  const ids = goal.criteria.map((c) => c.id);
  const json = extractJson(raw);
  const claimed = new Set(
    (Array.isArray(json?.met) ? json.met : []).filter(
      (x): x is string => typeof x === 'string' && ids.includes(x),
    ),
  );
  const met = ids.filter((id) => claimed.has(id));
  const remaining = ids.filter((id) => !claimed.has(id));
  const summary = typeof json?.summary === 'string' ? json.summary : '';
  return { met, remaining, summary, done: remaining.length === 0 && met.length > 0 };
}

/** Evaluate evidence (step results, diffs, verify output) against the goal. */
export async function evaluateGoal(
  goal: SessionGoal,
  evidence: string,
  ai: ChatClient,
  opts: { model: string; maxTokens?: number },
): Promise<GoalEvaluation> {
  const criteriaText = goal.criteria.map((c) => `${c.id}: ${c.text}`).join('\n');
  const reply = await ai.chat({
    system: EVAL_SYSTEM,
    messages: [
      {
        role: 'user',
        content:
          `GOAL: ${goal.statement}\n\nCRITERIA:\n${criteriaText}\n\n` +
          `EVIDENCE OF WORK SO FAR:\n${evidence.slice(0, 8000)}`,
      },
    ],
    reasoning: false,
    model: opts.model,
    maxTokens: opts.maxTokens ?? 800,
  });
  return parseGoalEvaluation(reply, goal);
}

/** Render the goal for banners and checkpoints. */
export function formatGoal(goal: SessionGoal): string[] {
  return [
    `goal: ${goal.statement}`,
    ...goal.criteria.map((c) => `  ${c.id}. ${c.text}`),
  ];
}
